import type { ErrorEvent } from '@sentry/gatsby';

const isFromOwnBundle = (filename: string) => {
  try {
    const url = new URL(filename);
    return (
      url.origin === window.location.origin && url.pathname.endsWith('.js')
    );
  } catch {
    return false;
  }
};

// Thrown by vendor scripts and browsers without a stack trace, so the message is all we can go on
const stacklessNoise = [
  'getLayoutMap() must be called from a top-level browsing context',
  'The I/O read operation failed',
  'Database deleted by request of the user',
  'Unable to load image data:image/svg+xml',
  /^Non-Error promise rejection captured with value: null$/,
];

const isStacklessNoise = (message = '') =>
  stacklessNoise.some((pattern) =>
    typeof pattern === 'string'
      ? message.includes(pattern)
      : pattern.test(message),
  );

// Vendor scripts, browser extensions and in-app browsers throw from code we don't ship:
// another origin, a script inlined into the page, or eval'd code (`<anonymous>` with a line number).
export const isThirdPartyError = (event: ErrorEvent) => {
  const exceptions = event.exception?.values ?? [];
  const exception = exceptions[exceptions.length - 1];
  const frames = exception?.stacktrace?.frames ?? [];
  // Native calls such as JSON.parse have no line number; skip them to reach the caller
  const throwingFrame = frames.filter((frame) => frame.lineno).pop();
  if (!throwingFrame?.filename) return isStacklessNoise(exception?.value);

  return !isFromOwnBundle(throwingFrame.filename);
};
